import Phaser from 'phaser';
import type { Pipe } from '../entities/Pipe';
import type { MoleculeMix, MoleculeType } from '../types';

const MOL_TEXTURE: Record<MoleculeType, string> = {
  food: 'mol-food',
  oxygen: 'mol-oxygen',
  co2: 'mol-co2',
  gruel: 'mol-gruel',
  waste: 'mol-waste',
  pulse: 'mol-pulse', // unused — spawnOne short-circuits for 'pulse', sprite never rendered
};

// How far (in pixels) one molecule travels per millisecond.
const MOL_SPEED_PX_PER_MS = 0.18;
// Spacing between molecules along the pipe (in pixels).
const MOL_SPACING_PX = 90;

/**
 * Animates molecule sprites traveling INSIDE a flowing pipe, from spout to receptacle.
 * Implementation: cycle through the pipe's segment centers, lerping a sprite's position
 * over each leg's duration. When a sprite reaches the end, despawn and respawn at the start.
 */
export class FlowFx {
  private sprites: Phaser.GameObjects.Image[] = [];
  private path: { x: number; y: number }[] = [];
  private legLengths: number[] = [];
  private totalLength = 0;
  private spawnTimer: Phaser.Time.TimerEvent | null = null;
  // Each sprite carries its progress as a userdata property.
  private updateHandler: () => void = () => {};
  private lastFrameTime = 0;
  private molIndex = 0;

  constructor(
    private scene: Phaser.Scene,
    private pipe: Pipe,
    private carries: MoleculeMix,
  ) {}

  /**
   * Replace the molecule mix this FlowFx cycles through. In-flight sprites
   * continue their tween with their existing textures; the NEXT spawnOne()
   * call uses the new mix. Used by Connector when an organ-output spout's
   * `transform` function returns a different mix mid-flow (e.g. intestines
   * side-out goes from `[oxygen]` passthrough to `[food, oxygen, co2]`
   * enriched once food arrives at the top input).
   */
  setCarries(newMix: MoleculeMix): void {
    this.carries = newMix;
  }

  start(): void {
    this.path = this.pipe.pathPoints();
    if (this.path.length < 2) return;
    this.legLengths = [];
    this.totalLength = 0;
    for (let i = 0; i < this.path.length - 1; i++) {
      const dx = this.path[i + 1].x - this.path[i].x;
      const dy = this.path[i + 1].y - this.path[i].y;
      const len = Math.hypot(dx, dy);
      this.legLengths.push(len);
      this.totalLength += len;
    }
    this.lastFrameTime = this.scene.time.now;

    // Spawn molecules at a fixed cadence; each one moves along the path independently.
    const spawnIntervalMs = MOL_SPACING_PX / MOL_SPEED_PX_PER_MS;
    this.spawnOne();
    this.spawnTimer = this.scene.time.addEvent({
      delay: spawnIntervalMs,
      loop: true,
      callback: () => this.spawnOne(),
    });

    this.updateHandler = () => this.tick();
    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.updateHandler);
  }

  stop(): void {
    if (this.spawnTimer) {
      this.spawnTimer.remove(false);
      this.spawnTimer = null;
    }
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.updateHandler);
    this.sprites.forEach((s) => s.destroy());
    this.sprites = [];
  }

  private spawnOne(): void {
    // Empty carries (rare — `setCarries([])` mid-flow or initial empty state):
    // skip this spawn. The timer keeps running so emission can resume later.
    if (this.carries.length === 0) return;
    const molType = this.carries[this.molIndex % this.carries.length];
    this.molIndex++;
    // 'pulse' is a mechanical-signal molecule (diaphragm → lungs) — no sprite.
    if (molType === 'pulse') return;
    const sprite = this.scene.add.image(this.path[0].x, this.path[0].y, MOL_TEXTURE[molType]);
    sprite.setDepth(8); // above pipes (5), below ports (20) — molecules disappear into the porthole
    (sprite as any).__progressPx = 0;
    this.sprites.push(sprite);
  }

  private tick(): void {
    const now = this.scene.time.now;
    const dt = now - this.lastFrameTime;
    this.lastFrameTime = now;

    const toRemove: Phaser.GameObjects.Image[] = [];
    for (const sprite of this.sprites) {
      const prev = (sprite as any).__progressPx as number;
      const next = prev + dt * MOL_SPEED_PX_PER_MS;
      if (next >= this.totalLength) {
        toRemove.push(sprite);
        continue;
      }
      (sprite as any).__progressPx = next;
      const pos = this.positionAlong(next);
      sprite.setPosition(pos.x, pos.y);
    }
    for (const s of toRemove) {
      s.destroy();
      const idx = this.sprites.indexOf(s);
      if (idx !== -1) this.sprites.splice(idx, 1);
    }
  }

  private positionAlong(distance: number): { x: number; y: number } {
    let remaining = distance;
    for (let i = 0; i < this.legLengths.length; i++) {
      const legLen = this.legLengths[i];
      if (remaining <= legLen) {
        const t = legLen === 0 ? 0 : remaining / legLen;
        return {
          x: this.path[i].x + (this.path[i + 1].x - this.path[i].x) * t,
          y: this.path[i].y + (this.path[i + 1].y - this.path[i].y) * t,
        };
      }
      remaining -= legLen;
    }
    return this.path[this.path.length - 1];
  }
}
